import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faExclamationTriangle,
    faShieldAlt,
    faDownload,
    faEye,
    faTimes,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../context/UserContext';
import { toast } from 'react-toastify';
import api from '../api/api';
import { routes } from '../config/utility';
import { ACCOUNT_DELETION_STATUS } from '../config/constants';

const AccountDeletionSection = () => {
    const { user } = useContext(UserContext);
    const [showDeletionForm, setShowDeletionForm] = useState(false);
    const [deletionRequest, setDeletionRequest] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        reason: '',
        confirmation_type: 'immediate',
        understand_consequences: false,
        password_confirmation: ''
    });
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successData, setSuccessData] = useState(null);

    // Check existing deletion request
    const checkDeletionStatus = async () => {
        try {
            setIsLoading(true);
            const response = await api.post('/account/deletion-status', { email: user.email });

            if (response.data.success && response.data.data.has_request) {
                setDeletionRequest(response.data.data.request);
            } else {
                setDeletionRequest(null);
            }
        } catch (error) {
            console.error('Error checking deletion status:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Submit deletion request
    const handleDeletionRequest = async (e) => {
        e.preventDefault();

        if (!formData.understand_consequences) {
            toast.error('Please confirm that you understand the consequences');
            return;
        }

        try {
            setIsLoading(true);
            const response = await api.post('/account/request-deletion-authenticated', {
                ...formData,
                email: user.email
            });

            if (response.data) {
                // Store success data and show modal
                setSuccessData(response.data);
                setShowSuccessModal(true);
                setShowDeletionForm(false);
                checkDeletionStatus();
            } else {
                toast.error(response.data.message || 'Failed to submit deletion request');
            }
        } catch (error) {
            console.error('Deletion request error:', error);
            toast.error(error.response?.data?.message || 'Failed to submit deletion request');
        } finally {
            setIsLoading(false);
        }
    };

    // Cancel deletion request
    const handleCancelRequest = async () => {
        if (!window.confirm('Are you sure you want to cancel your deletion request?')) {
            return;
        }

        try {
            setIsLoading(true);
            const response = await api.post('/account/cancel-deletion', { email: user.email });

            if (response.data.success) {
                toast.success('Deletion request cancelled');
                setDeletionRequest(null);
            } else {
                toast.error(response.data.message || 'Failed to cancel deletion request');
            }
        } catch (error) {
            console.error('Cancel error:', error);
            toast.error(error.response?.data?.message || 'Failed to cancel deletion request');
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case ACCOUNT_DELETION_STATUS.PENDING:
                return <FontAwesomeIcon icon={faEye} className="text-yellow-500" />;
            case ACCOUNT_DELETION_STATUS.VERIFIED:
                return <FontAwesomeIcon icon={faCheck} className="text-blue-500" />;
            case ACCOUNT_DELETION_STATUS.PROCESSING:
                return <FontAwesomeIcon icon={faShieldAlt} className="text-blue-500" />;
            case ACCOUNT_DELETION_STATUS.COMPLETED:
                return <FontAwesomeIcon icon={faCheck} className="text-green-500" />;
            case ACCOUNT_DELETION_STATUS.REJECTED:
                return <FontAwesomeIcon icon={faTimes} className="text-red-500" />;
            default:
                return <FontAwesomeIcon icon={faEye} className="text-gray-500" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case ACCOUNT_DELETION_STATUS.PENDING:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case ACCOUNT_DELETION_STATUS.VERIFIED:
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case ACCOUNT_DELETION_STATUS.PROCESSING:
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case ACCOUNT_DELETION_STATUS.COMPLETED:
                return 'bg-green-100 text-green-800 border-green-200';
            case ACCOUNT_DELETION_STATUS.REJECTED:
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    return (
        <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-primary flex items-center">
                    <FontAwesomeIcon icon={faTrash} className="mr-3" />
                    Account Deletion
                </h3>
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faTrash} className="text-red-500" />
                </div>
            </div>

            {/* Warning Message */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-1" />
                    <div>
                        <h4 className="font-semibold text-red-800 mb-2">Important Notice</h4>
                        <p className="text-red-700 text-sm mb-3">
                            Deleting your account will permanently remove all your data, including:
                        </p>
                        <ul className="text-red-700 text-sm space-y-1">
                            <li>• All pet profiles and information</li>
                            <li>• Walk and delivery history</li>
                            <li>• Membership and payment records</li>
                            <li>• All personal data and preferences</li>
                        </ul>
                        <p className="text-red-700 text-sm mt-3 font-semibold">
                            This action cannot be undone!
                        </p>
                    </div>
                </div>
            </div>

            {/* Current Status */}
            {deletionRequest ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-4"
                >
                    <div className={`border rounded-2xl p-4 ${getStatusColor(deletionRequest.status)}`}>
                        <div className="flex items-center gap-3 mb-2">
                            {getStatusIcon(deletionRequest.status)}
                            <span className="font-semibold capitalize">
                                Status: {deletionRequest.status}
                            </span>
                        </div>
                        <p className="text-sm">
                            Requested: {new Date(deletionRequest.requested_at).toLocaleString()}
                        </p>
                        {deletionRequest.reason && (
                            <p className="text-sm mt-2">
                                <strong>Reason:</strong> {deletionRequest.reason}
                            </p>
                        )}
                    </div>

                    {/* Actions based on status */}
                    {deletionRequest.status === ACCOUNT_DELETION_STATUS.PENDING && !deletionRequest.verified_at && (
                        <button
                            onClick={handleCancelRequest}
                            disabled={isLoading}
                            className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white py-3 rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                            Cancel Deletion Request
                        </button>
                    )}
                </motion.div>
            ) : (
                <div className="space-y-4">
                    <p className="text-gray-600">
                        If you want to delete your account, click the button below to start the process.
                    </p>

                    <button
                        onClick={() => setShowDeletionForm(true)}
                        className="w-full bg-red-500 hover:bg-red-600 text-white py-4 rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                        Request Account Deletion
                    </button>
                </div>
            )}

            {/* Deletion Form Modal - Mobile Responsive */}
            {showDeletionForm && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50"
                    onClick={() => setShowDeletionForm(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-md w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl sm:text-2xl font-bold text-red-600 mb-4 sm:mb-6">Confirm Account Deletion</h3>

                        <form onSubmit={handleDeletionRequest} className="space-y-3 sm:space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    Reason for deletion (optional)
                                </label>
                                <textarea
                                    value={formData.reason}
                                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                                    placeholder="Help us improve by sharing why you're leaving..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    Data handling
                                </label>
                                <select
                                    value={formData.confirmation_type}
                                    onChange={(e) => setFormData({ ...formData, confirmation_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                                >
                                    <option value="immediate">Delete immediately</option>
                                    <option value="data_export_both">Export data and delete</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                                    Current Password
                                </label>
                                <input
                                    type="password"
                                    value={formData.password_confirmation}
                                    onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
                                    placeholder="Enter your current password"
                                />
                            </div>

                            <div className="flex items-start gap-2 sm:gap-3">
                                <input
                                    type="checkbox"
                                    id="understand"
                                    checked={formData.understand_consequences}
                                    onChange={(e) => setFormData({ ...formData, understand_consequences: e.target.checked })}
                                    className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded flex-shrink-0"
                                />
                                <label htmlFor="understand" className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                                    I understand that this action cannot be undone and all my data will be permanently deleted.
                                </label>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowDeletionForm(false)}
                                    className="w-full sm:flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 text-sm sm:text-base"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full sm:flex-1 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            <span className="hidden sm:inline">Submitting...</span>
                                            <span className="sm:hidden">Submitting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faTrash} />
                                            <span className="hidden sm:inline">Delete Account</span>
                                            <span className="sm:hidden">Delete</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}

            {/* Success Modal - Mobile Responsive */}
            {showSuccessModal && successData && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center p-2 sm:p-4 z-50"
                    onClick={() => setShowSuccessModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 max-w-sm sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="text-center mb-6 sm:mb-8">
                            <div className="bg-green-100 rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <FontAwesomeIcon icon={faCheck} className="text-green-500 text-2xl sm:text-3xl" />
                            </div>
                            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-green-600 mb-2">Request Submitted Successfully!</h3>
                            <p className="text-sm sm:text-base text-gray-600">{successData.message}</p>
                        </div>

                        {/* Request ID */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                            <div className="flex items-center gap-2 mb-2">
                                <FontAwesomeIcon icon={faEye} className="text-blue-600 text-sm sm:text-base" />
                                <span className="font-semibold text-blue-800 text-sm sm:text-base">Request ID</span>
                            </div>
                            <p className="text-blue-700 font-mono text-sm sm:text-base lg:text-lg break-all">{successData.request_id}</p>
                        </div>



                        {/* Deletion Process Info */}
                        {successData.deletion_process_info && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                                <h4 className="font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                                    <FontAwesomeIcon icon={faShieldAlt} className="text-gray-600 text-sm sm:text-base" />
                                    Process Information
                                </h4>

                                <div className="space-y-2 sm:space-y-3">
                                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                        <span className="text-gray-600 text-xs sm:text-sm">Estimated Completion:</span>
                                        <span className="font-semibold text-xs sm:text-sm">{successData.deletion_process_info.estimated_completion_time}</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                        <span className="text-gray-600 text-xs sm:text-sm">Data Retention:</span>
                                        <span className="font-semibold text-xs sm:text-sm">{successData.deletion_process_info.data_retention_period}</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                        <span className="text-gray-600 text-xs sm:text-sm">Billing:</span>
                                        <span className="font-semibold text-xs sm:text-sm">{successData.deletion_process_info.billing_handling}</span>
                                    </div>
                                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                        <span className="text-gray-600 text-xs sm:text-sm">Support:</span>
                                        <span className="font-semibold text-blue-600 text-xs sm:text-sm break-all">{successData.deletion_process_info.support_contact}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Next Steps */}
                        {successData.next_steps && (
                            <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                                <h4 className="font-semibold text-green-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                                    <FontAwesomeIcon icon={faCheck} className="text-green-600 text-sm sm:text-base" />
                                    Next Steps
                                </h4>

                                <div className="space-y-2 sm:space-y-3">
                                    {Object.entries(successData.next_steps).map(([step, description]) => (
                                        <div key={step} className="flex items-start gap-2 sm:gap-3">
                                            <div className="bg-green-600 text-white rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0">
                                                {step}
                                            </div>
                                            <p className="text-green-700 text-xs sm:text-sm leading-relaxed">{description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 sm:gap-3">
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="flex-1 bg-primary hover:bg-primary-dark text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl font-semibold transition-colors duration-200 text-sm sm:text-base"
                            >
                                Got it, Thanks!
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
};

export default AccountDeletionSection;
